/**
 * Format date to readable string
 */
export const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Format date and time
 */
export const formatDateTime = (date) => {
  return new Date(date).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Truncate text with ellipsis
 */
export const truncateText = (text, maxLength = 100) => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

/**
 * Debounce function
 */
export const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * Validate email format
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * Generate random ID
 */
export const generateId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

function getOrdinalSuffix(n) {
  const s = ['th', 'st', 'nd', 'rd']
  const v = n % 100
  return s[(v - 20) % 10] || s[v] || s[0]
}

/**
 * Format a UTC datetime string for session date display (e.g., "Tue, Dec 2nd").
 * Converts to the user's local timezone via the browser.
 */
export const formatSessionDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const dayName = days[date.getDay()]
  const month = months[date.getMonth()]
  const dayNum = date.getDate()
  return `${dayName}, ${month} ${dayNum}${getOrdinalSuffix(dayNum)}`
}

/**
 * Format a UTC datetime range for session time display (e.g., "9AM-10AM").
 * Converts to the user's local timezone via the browser.
 */
export const formatSessionTime = (startStr, endStr) => {
  if (!startStr) return ''
  const formatTime = (date) => {
    let hours = date.getHours()
    const minutes = date.getMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    if (minutes > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}${ampm}`
    }
    return `${hours}${ampm}`
  }
  const startTime = formatTime(new Date(startStr))
  if (endStr) {
    const endTime = formatTime(new Date(endStr))
    return `${startTime}-${endTime}`
  }
  return startTime
}

/**
 * Convert a UTC datetime string to a local datetime-local input value (YYYY-MM-DDTHH:mm).
 * Used when populating datetime-local inputs with server-stored UTC dates.
 */
export const toLocalDatetimeString = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

